<script setup>
// Текст повідомлення чату разом із картинками — і в чаті сайту, і в чаті
// Telegram (там картинки приходять окремо, пропом images).
//
// Люди кидають у чат просто посилання на зображення — показуємо його
// картинкою, а не голим URL. За замовчуванням картинка розмита: у прямому
// ефірі кинути гидоту може будь-хто, і глядач має вирішувати сам, чи
// дивитись. Клік знімає розмиття (для цієї картинки й до перезавантаження
// сторінки).
//
// Малюємо через <img>, тобто виконати щось стороннє картинка не може.
// referrerpolicy — щоб чужий сайт не бачив, звідки на нього дивляться.

const props = defineProps({
  text: { type: String, default: '' },
  // Картинки, які прийшли окремо від тексту: у Telegram зображення — це
  // вкладення, а не посилання, тож розпізнавати в тексті там нічого. Правила
  // показу ті самі, що й для знайдених посилань, — спершу під спойлером
  images: { type: Array, default: () => [] },
  // Скільки картинок показуємо з одного повідомлення: решта лишається
  // посиланнями, інакше одним повідомленням можна залити весь чат
  max: { type: Number, default: 3 },
  // Текст іде тим самим рядком, що й нік перед ним (чат ефіру), а не
  // окремим абзацом під ним (коментарі під плеєром, де є ще й аватарка
  // з датою)
  inline: { type: Boolean, default: false },
  // Чиє імʼя виділяти в «@згадках» — див. EmoteText
  me: { type: String, default: '' }
})

// Розширення беремо явним списком, а не «будь-що з крапкою»: інакше
// картинкою став би будь-який лінк, і зламане зображення висіло б у чаті
// замість клікабельного посилання.
// SVG свідомо немає: він уміє тягнути зовнішні ресурси й ховати всередині
// що завгодно, а користі в чаті з нього нуль
const IMAGE_URL = /https?:\/\/[^\s<>"']+\.(?:jpe?g|png|gif|webp|avif|bmp)(?:\?[^\s<>"']*)?/gi

const parsed = computed(() => {
  const raw = props.text || ''
  const found = raw.match(IMAGE_URL) || []

  // Однакове посилання двічі — це те саме зображення, а не два
  const images = [...new Set([...props.images, ...found])].slice(0, props.max)

  // Прибираємо з тексту саме ті посилання, які показали картинкою: лишати
  // ще й URL поруч із нею — це подвійне повідомлення. Ті, що не влізли в
  // ліміт, лишаються в тексті посиланнями
  const text = images
    .reduce((acc, url) => acc.split(url).join(' '), raw)
    .replace(/\s+/g, ' ')
    .trim()

  return { text, images }
})

// Спойлер у тексті: ||так||. Закрите розмите, клік показує. Розбираємо вже
// після картинок, щоб посилання на картинку всередині не губилось
const SPOILER = /\|\|([^|]+?)\|\|/g

const segments = computed(() => {
  const source = parsed.value.text
  const out = []
  let last = 0

  for (const match of source.matchAll(SPOILER)) {
    if (match.index > last) out.push({ text: source.slice(last, match.index) })
    out.push({ spoiler: match[1].trim(), key: match.index })
    last = match.index + match[0].length
  }

  if (last < source.length) out.push({ text: source.slice(last) })
  return out
})

const openedSpoilers = ref(new Set())

const toggleSpoiler = (key) => {
  const next = new Set(openedSpoilers.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  openedSpoilers.value = next
}

// Знята розмитість — по конкретному URL. Свідомо не зберігаємо: наступного
// разу картинка знову прийде закритою
const revealed = ref(new Set())

// Гіфки з пошуку в чаті (isGifUrl — тільки CDN Tenor/GIPHY, відібрані
// фільтром провайдера) показуємо одразу: розмита гіфка, яку людина щойно
// свідомо вибрала з панелі, — це просто зламана функція. Клік так само
// перемикає — гіфку можна й розмити
const isOpen = (url) => revealed.value.has(url) !== isGifUrl(url)

const toggleReveal = (url) => {
  const next = new Set(revealed.value)
  if (next.has(url)) next.delete(url)
  else next.add(url)
  revealed.value = next
}

// Зображення, яке не завантажилось, ховаємо: порожня рамка з «поламаною»
// іконкою в чаті виглядає як збій сайту
const broken = ref(new Set())

const onError = (url) => {
  broken.value = new Set(broken.value).add(url)
}

const visibleImages = computed(() =>
  parsed.value.images.filter((url) => !broken.value.has(url))
)
</script>

<template>
  <!--
    В інлайновому режимі і сама обгортка, і текст лишаються рядковими —
    інакше «нік:» і повідомлення розʼїжджаються на два рядки. Картинки
    нижче все одно блокові, тож вони самі переносяться під текст
  -->
  <div :class="inline ? 'inline' : 'min-w-0'">
    <template
      v-for="(part, index) in segments"
      :key="index"
    >
      <span
        v-if="part.spoiler"
        class="bg-base-content/10 rounded px-0.5 cursor-pointer transition-[filter] duration-200"
        :class="openedSpoilers.has(part.key) ? '' : 'blur-sm select-none'"
        :title="openedSpoilers.has(part.key) ? 'Натисніть, щоб знову приховати' : 'Спойлер — натисніть, щоб показати'"
        @click.stop="toggleSpoiler(part.key)"
      >
        <EmoteText
          :class="'text-sm'"
          :text="part.spoiler"
          :me="me"
        />
      </span>
      <EmoteText
        v-else
        :class="inline ? 'text-sm' : 'block text-sm'"
        :text="part.text"
        :me="me"
      />
    </template>

    <div
      v-if="visibleImages.length"
      class="flex flex-col gap-1 mt-1"
    >
      <div
        v-for="url in visibleImages"
        :key="url"
        class="group/media relative bg-base-200 rounded max-w-[240px] overflow-hidden cursor-pointer"
        :title="
          isOpen(url)
            ? 'Натисніть, щоб знову приховати'
            : 'Натисніть, щоб показати зображення'
        "
        @click="toggleReveal(url)"
      >
        <img
          :src="url"
          class="block w-full max-h-[220px] object-contain transition-[filter] duration-200"
          :class="isOpen(url) ? '' : 'blur-xl scale-105'"
          loading="lazy"
          decoding="async"
          referrerpolicy="no-referrer"
          alt=""
          @error="onError(url)"
        />

        <!-- Оригінал сусідньою вкладкою: у стрічці картинка обрізана до
             240 пікселів. Поверх закритого спойлера кнопки немає — вона
             обходила б саме те, заради чого розмиття й ставили -->
        <a
          v-if="isOpen(url)"
          :href="url"
          target="_blank"
          rel="noopener noreferrer"
          class="top-1 right-1 absolute flex items-center gap-1 bg-base-200/90 opacity-0 group-hover/media:opacity-100 px-1.5 py-0.5 rounded text-[10px] text-base-content/80 hover:text-primary transition-opacity"
          title="Відкрити оригінал у новій вкладці"
          @click.stop
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line
              x1="10"
              y1="14"
              x2="21"
              y2="3"
            />
          </svg>
          оригінал
        </a>

        <!--
          Підказка поверх розмитого: без неї сірий прямокутник читається як
          зламана картинка, а не як «клікни, щоб подивитись»
        -->
        <div
          v-if="!isOpen(url)"
          class="absolute inset-0 flex flex-col justify-center items-center gap-1 bg-base-300/40 text-[11px] text-base-content/80"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          Показати зображення
        </div>
      </div>
    </div>
  </div>
</template>
